import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getVenuaPlannerSerieTransportSwapImpact,
  getVenuaPlannerTermTransportSwapImpact,
  resolveVenuaPlannerSerieTransportChain,
  resolveVenuaPlannerTermTransportChain,
  useGetVenuaPlannerSerieTransportScheme,
  useGetVenuaPlannerTermTransportScheme,
  useSaveVenuaPlannerSerieTransportScheme,
  useSaveVenuaPlannerTermTransportScheme,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import type {
  ConnectedFlightResponse,
  GetTransportSchemeResponse,
  GetTransportSwapImpactResponse,
  ResolveChainMutationRequest,
  ResolveTransportChainResponse,
  TransportSchemeDirectionResponse,
} from '@/api/generated/schemas';
import {
  ChainMutationKind,
  TransportSchemeKind,
  TransportSchemeTicketKind,
} from '@/api/generated/schemas';
import {
  getGetVenuaPlannerCalendarTermsQueryKey,
  getGetVenuaPlannerSerieDetailQueryKey,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { venuaPlannerQueryKeys } from '../../../venuaPlannerApi';
import { invalidateCalculationScopes, invalidateTransportSchemes } from '../useChangeLayerState';
import { uuidv7 } from '@/shared/lib/uuidv7';
import { serverErrorDetail } from '@/shared/lib/problemDetails';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import { toast } from '@/shared/ui';
import {
  modeOf,
  toDraft,
  toSaveRequest,
  type TransportSchemeDraft,
  type TransportSchemeScope,
} from './model/types';

interface Options {
  productId: string;
  serieId: string;
  termId?: string;
  scope: TransportSchemeScope;
  /** Dotaz běží jen když je záložka opravdu vidět. */
  enabled: boolean;
}

export interface TransportSchemeEditor {
  isLoading: boolean;
  scheme: GetTransportSchemeResponse | undefined;
  draft: TransportSchemeDraft | null;
  /** Rozřešené směry — po mutaci z odpovědi `chain`, jinak z `GetTransportScheme`. */
  outbound: TransportSchemeDirectionResponse | undefined;
  inbound: TransportSchemeDirectionResponse | undefined;
  isDirty: boolean;
  isMutating: boolean;
  isSubmitting: boolean;
  /** Spojené lety — po mutaci z odpovědi `chain`, jinak z `GetTransportScheme`. */
  connectedFlights: ConnectedFlightResponse[];
  addTransport: (
    regularTransportId: number,
    direction: 'Outbound' | 'Inbound',
    insertAt?: number
  ) => Promise<void>;
  addConnectedFlight: (regularTransportId: number) => Promise<void>;
  removeTicket: (ticketId: string) => Promise<void>;
  /** Prohodí směry kombinované dopravy i s jejich obsahem (stanice a čas busu, řetěz doprav). */
  flipKind: () => Promise<void>;
  /** Čerstvý dopad prohození ze serveru — čím se řídí potvrzovací dotaz. Nikdy z keše. */
  loadSwapImpact: () => Promise<GetTransportSwapImpactResponse>;
  patchDraft: (patch: Partial<TransportSchemeDraft>) => void;
  submit: () => Promise<boolean>;
}

/**
 * Stav záložky Doprava.
 *
 * Hook se volá **na úrovni stránky**, aby spodní akční lišta (`activeSubmit`) fungovala beze změny a
 * nevznikal `onSubmitReady` callback — inline arrow v něm dřív působil nekonečný render loop.
 *
 * S řetězem se nemanipuluje lokálně: přidání i odebrání jde na server (endpoint `chain`), který vrátí
 * hotový seznam ticketů včetně přečíslování. Klient nepočítá ani kolik ticketů vznikne.
 */
export function useTransportSchemeEditor(options: Options): TransportSchemeEditor {
  const { productId, serieId, termId, scope, enabled } = options;
  const queryClient = useQueryClient();
  const { t } = useTranslation('venua-planner');

  const serieQuery = useGetVenuaPlannerSerieTransportScheme(productId, serieId, {
    query: { enabled: enabled && scope === 'serie', staleTime: DETAIL_QUERY_STALE_TIME_MS },
  });
  const termQuery = useGetVenuaPlannerTermTransportScheme(productId, serieId, termId ?? '', {
    query: {
      enabled: enabled && scope === 'term' && !!termId,
      staleTime: DETAIL_QUERY_STALE_TIME_MS,
    },
  });

  const query = scope === 'serie' ? serieQuery : termQuery;
  const scheme = query.data;

  const saveSerie = useSaveVenuaPlannerSerieTransportScheme();
  const saveTerm = useSaveVenuaPlannerTermTransportScheme();

  const [draft, setDraft] = useState<TransportSchemeDraft | null>(null);
  const [resolved, setResolved] = useState<ResolveTransportChainResponse | null>(null);
  const [isDirty, setDirty] = useState(false);
  const [isMutating, setMutating] = useState(false);

  // Draft se odvodí znovu i při výměně efektivní definice (přepnutí Sdíleno ↔ Vlastní), ne jen po uložení.
  const draftKey = scheme ? `${scheme.transportSchemeId}:${scheme.isOwnScheme}` : null;

  useEffect(() => {
    if (!scheme) return;
    setDraft(toDraft(scheme));
    setResolved(null);
    setDirty(false);
  }, [draftKey, scheme]);

  const mutateChain = useCallback(
    async (
      mutation: ResolveChainMutationRequest,
      /**
       * Prohození směrů mění i to, co se serveru posílá a co se pak drží v draftu: řetěz se počítá
       * proti **novému** druhu schématu a autobusová strana (stanice, čas) si vymění směry.
       */
      flipTo?: TransportSchemeDraft['kind']
    ) => {
      if (!draft) return;
      setMutating(true);
      // Selhání se nechytá: doprava se nepřidá (bez rozřešení by řádek nebyl kompletní) a hlášku ukazuje
      // volající — z původní chyby, protože jen ta nese serverový důvod.
      try {
        const body = {
          // Posílají se i spojené lety — řetěz neovlivní, ale server je musí vidět, aby je vrátil zpět
          // (a rovnou zvalidoval proti hubům). Autobusový ticket dopravu nemá, ten zůstává lokálně.
          tickets: draft.tickets
            .filter((t) => t.regularTransportId != null)
            .map((t) => ({
              ticketId: t.ticketId,
              regularTransportId: t.regularTransportId as number,
              outboundSequence: t.outboundSequence,
              inboundSequence: t.inboundSequence,
              kind: t.kind,
            })),
          mutation,
          // Podle druhu schématu server pozná, které směry jsou letecké — u kombinované dopravy do
          // autobusového protisměru nesmí nic přidat. Bere se z draftu, protože přepnutí varianty
          // ještě nemusí být uložené.
          kind: flipTo ?? draft.kind,
        };

        const response =
          scope === 'serie'
            ? await resolveVenuaPlannerSerieTransportChain(productId, serieId, body)
            : await resolveVenuaPlannerTermTransportChain(productId, serieId, termId ?? '', body);

        setResolved(response);
        setDraft((current) => {
          if (!current) return current;
          const next = applyChain(current, response);
          return flipTo
            ? {
                ...next,
                kind: flipTo,
                outbound: next.inbound,
                inbound: next.outbound,
                // Server prohozením zahodí přiřazené trasy všech termínů (linka patří autobusovému
                // směru) — draft to musí ukázat hned, ne až po znovunačtení.
                termDirections: next.termDirections.map((direction) => ({
                  ...direction,
                  stationChainId: null,
                  stationChainName: null,
                  stationChainReversed: false,
                })),
              }
            : next;
        });
        setDirty(true);
      } finally {
        setMutating(false);
      }
    },
    [draft, productId, scope, serieId, termId]
  );

  const addTransport = useCallback(
    async (regularTransportId: number, direction: 'Outbound' | 'Inbound', insertAt?: number) => {
      await mutateChain({
        kind: ChainMutationKind.Add,
        regularTransportId,
        direction,
        insertAt: insertAt ?? null,
        ticketId: null,
        // Klient neví, jestli doprava naplní jeden nebo oba směry — pošle ID na obě možnosti.
        newTicketIds: [uuidv7(), uuidv7()],
      });
    },
    [mutateChain]
  );

  const addConnectedFlight = useCallback(
    async (regularTransportId: number) => {
      await mutateChain({
        kind: ChainMutationKind.AddConnected,
        regularTransportId,
        direction: null,
        insertAt: null,
        ticketId: null,
        // Rotace je vždycky jeden ticket — na rozdíl od hlavní dopravy tu není co rozhodovat.
        newTicketIds: [uuidv7()],
      });
    },
    [mutateChain]
  );

  const removeTicket = useCallback(
    async (ticketId: string) => {
      await mutateChain({
        kind: ChainMutationKind.Remove,
        regularTransportId: null,
        direction: null,
        insertAt: null,
        ticketId,
        newTicketIds: null,
      });
    },
    [mutateChain]
  );

  /**
   * Dopad prohození se čte přímo ze serveru, **mimo query keš**. Přiřazenou trasu jde nastavit i jinde
   * než tady (detail termínu, kalendář), takže načtená odpověď schématu o ní vědět nemusí — a řídit se
   * jí by znamenalo prohodit bez varování a o trasy tiše přijít.
   */
  const loadSwapImpact = useCallback(
    () =>
      scope === 'serie'
        ? getVenuaPlannerSerieTransportSwapImpact(productId, serieId)
        : getVenuaPlannerTermTransportSwapImpact(productId, serieId, termId ?? ''),
    [productId, scope, serieId, termId]
  );

  const flipKind = useCallback(async () => {
    if (!draft) return;
    await mutateChain(
      {
        kind: ChainMutationKind.Flip,
        regularTransportId: null,
        direction: null,
        insertAt: null,
        ticketId: null,
        newTicketIds: null,
      },
      // Prohození je vlastní inverze — druhá varianta kombinované dopravy.
      draft.kind === TransportSchemeKind.AirBus
        ? TransportSchemeKind.BusAir
        : TransportSchemeKind.AirBus
    );
  }, [draft, mutateChain]);

  const patchDraft = useCallback((patch: Partial<TransportSchemeDraft>) => {
    setDraft((current) => (current ? { ...current, ...patch } : current));
    setDirty(true);
  }, []);

  const submit = useCallback(async (): Promise<boolean> => {
    if (!draft) return false;
    const data = toSaveRequest(draft);
    try {
      if (scope === 'serie') {
        await saveSerie.mutateAsync({ productId, serieId, data });
      } else {
        await saveTerm.mutateAsync({ productId, serieId, termId: termId ?? '', data });
      }

      await invalidateTransportSchemes(queryClient, { productId, serieId });
      // Kapacita (společná i vlastní termínu) se čte i jinde než tady — v hlavičce a tabulce termínů
      // na první záložce, ve stromu a v kalendáři. Bez invalidace tam zůstane stará hodnota.
      // Ceny ticketů navíc vstupují do nákladu termínu, takže se mění i marže — proto jde s nimi
      // i kalkulace a ekonomika (sloupec Kapacita v tabulce termínů jede taky z ekonomiky).
      invalidateCalculationScopes(queryClient, { productId, serieId });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getGetVenuaPlannerSerieDetailQueryKey(productId, serieId),
        }),
        queryClient.invalidateQueries({ queryKey: getGetVenuaPlannerCalendarTermsQueryKey() }),
        queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() }),
      ]);

      setDirty(false);
      return true;
    } catch (error) {
      // Bez hlášky vypadá odmítnuté uložení jako by se nic nestalo. Konkrétní důvod ale zná server
      // (uzamčená vrstva, archivovaný seriál, konflikt definice…), takže se ukazuje jeho věta; obecná
      // rada „načtěte záložku znovu“ zůstává jen pro případ, kdy odpověď důvod nenese (výpadek sítě).
      toast({
        severity: 'error',
        title: t('transportScheme.save.errorTitle'),
        description: serverErrorDetail(error) ?? t('transportScheme.save.error'),
      });
      return false;
    }
  }, [draft, productId, queryClient, saveSerie, saveTerm, scope, serieId, t, termId]);

  // Režim směru plyne z druhu schématu v draftu, ne z odpovědi — přepnutí kombinované varianty se musí
  // v panelech projevit hned, ještě před uložením.
  const kind = draft?.kind ?? scheme?.kind;

  const directions = useMemo(() => {
    const withMode = (
      direction: TransportSchemeDirectionResponse | undefined,
      which: 'Outbound' | 'Inbound'
    ) => (direction && kind ? { ...direction, mode: modeOf(kind, which) } : direction);

    return {
      outbound: withMode(resolved?.outbound ?? scheme?.outbound, 'Outbound'),
      inbound: withMode(resolved?.inbound ?? scheme?.inbound, 'Inbound'),
      connectedFlights: resolved?.connectedFlights ?? scheme?.connectedFlights ?? [],
    };
  }, [kind, resolved, scheme]);

  return {
    isLoading: query.isLoading,
    scheme,
    draft,
    outbound: directions.outbound,
    inbound: directions.inbound,
    connectedFlights: directions.connectedFlights,
    isDirty,
    isMutating,
    isSubmitting: saveSerie.isPending || saveTerm.isPending,
    addTransport,
    addConnectedFlight,
    removeTicket,
    flipKind,
    loadSwapImpact,
    patchDraft,
    submit,
  };
}

/**
 * Promítne odpověď `chain` do draftu: server je jediná autorita na seznam ticketů a jejich sekvence,
 * ceny už zadané u zachovaných ticketů ale zůstávají.
 */
function applyChain(
  draft: TransportSchemeDraft,
  response: ResolveTransportChainResponse
): TransportSchemeDraft {
  const previous = new Map(draft.tickets.map((t) => [t.ticketId, t]));
  // Tickety bez dopravy (autobusová strana) server nevidí, takže se drží lokálně.
  const local = draft.tickets.filter((t) => t.regularTransportId == null);

  const fromServer = response.tickets.map((t) => {
    const existing = previous.get(t.ticketId);
    return {
      ticketId: t.ticketId,
      // `Kind` má na kontraktu default kvůli starším klientům, ale server ho vždycky vyplní.
      kind: t.kind ?? TransportSchemeTicketKind.Primary,
      regularTransportId: t.regularTransportId,
      regularTransportName: existing?.regularTransportName ?? null,
      outboundSequence: t.outboundSequence,
      inboundSequence: t.inboundSequence,
      expectedPaxAmount: existing?.expectedPaxAmount ?? null,
      expectedGroupAmount: existing?.expectedGroupAmount ?? null,
      manualCostTermCount: existing?.manualCostTermCount ?? 0,
    };
  });

  const tickets = [...fromServer, ...local];
  const ticketIds = new Set(tickets.map((t) => t.ticketId));

  // Řádky matice musí mít cenovou buňku pro každý ticket — nové se přidají prázdné, zrušené odejdou.
  const rows = draft.rows.map((row) => ({
    ...row,
    costs: tickets.map(
      (ticket) =>
        row.costs.find((c) => c.ticketId === ticket.ticketId) ?? {
          ticketId: ticket.ticketId,
          paxAmount: null,
          groupAmount: null,
          isManual: false,
        }
    ),
  }));

  return {
    ...draft,
    tickets,
    rows: rows.map((row) => ({
      ...row,
      costs: row.costs.filter((c) => ticketIds.has(c.ticketId)),
    })),
  };
}
